// ==============================================
// FILE: frontend/src/services/api.service.js
// ==============================================

import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../config/api.config';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
    });

    // Request interceptor - Add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors globally
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ==================== AUTH METHODS ====================

  getToken() {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  setToken(token) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  clearAuth() {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.IS_ADMIN_LOGGED_IN);
  }

  // ==================== GENERIC HTTP METHODS ====================

  async request(method, url, data, config) {
    try {
      const response = await this.api.request({
        method,
        url,
        data,
        ...config,
      });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'An error occurred';
      throw new Error(errorMessage);
    }
  }

  async get(url, config) {
    return this.request('GET', url, undefined, config);
  }

  async post(url, data, config) {
    return this.request('POST', url, data, config);
  }

  async put(url, data, config) {
    return this.request('PUT', url, data, config);
  }

  async patch(url, data, config) {
    return this.request('PATCH', url, data, config);
  }

  async delete(url, config) {
    return this.request('DELETE', url, undefined, config);
  }

  async upload(url, formData) {
    return this.request('POST', url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // ==================== API METHODS ====================

  // ========== AUTH APIs ==========

  async login(email, password) {
    const response = await this.post('/auth/login', { email, password });
    if (response.success && response.data?.token) {
      this.setToken(response.data.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
    }
    return response;
  }

  async requestAdminAccess(fullName, email, securityCode) {
    return this.post('/auth/admin/request', {
      full_name: fullName,
      email,
      security_code: securityCode,
    });
  }

  async completeAdminRegistration(email, approvalCode, password) {
    const response = await this.post('/auth/admin/complete', {
      email,
      approval_code: approvalCode,
      password,
    });
    if (response.success && response.data?.token) {
      this.setToken(response.data.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
    }
    return response;
  }

  logout() {
    this.clearAuth();
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  // ========== SUBMISSION APIs (Public) ==========

  async createSubmission(username, fields) {
    return this.post('/submissions', { username, fields });
  }

  async uploadImages(fieldId, files) {
    const formData = new FormData();
    formData.append('field_id', fieldId);
    files.forEach((file) => {
      formData.append('images', file);
    });
    return this.upload('/submissions/upload', formData);
  }

  // ========== ADMIN APIs (Protected) ==========

  async getAdminProfile() {
    return this.get('/admin/profile');
  }

  async getDashboardStats() {
    return this.get('/admin/dashboard/stats');
  }

  async getPendingAdminRequests() {
    return this.get('/admin/requests/pending');
  }

  async getAllSubmissions(page = 1, limit = 10, status) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) {
      params.append('status', status);
    }
    return this.get(`/admin/submissions?${params.toString()}`);
  }

  async getSubmissionById(id) {
    return this.get(`/admin/submissions/${id}`);
  }

  async updateSubmissionStatus(id, status) {
    return this.patch(`/admin/submissions/${id}/status`, { status });
  }

  async deleteSubmission(id) {
    return this.delete(`/admin/submissions/${id}`);
  }

  // ========== SUPER ADMIN APIs ==========

  async approvePendingRequest(email) {
    return this.post(`/admin/requests/${email}/approve`);
  }

  async rejectPendingRequest(email) {
    return this.post(`/admin/requests/${email}/reject`);
  }
}

// Export singleton instance
export const apiService = new ApiService();